import { call, put, all, PutEffect, takeEvery, CallEffect } from 'redux-saga/effects';
import {
    ConsentActionType,
    GetUserConsentForClient,
    registerConsent,
    RegisterConsent,
    setClientInfo,
    SetClientInfo,
    setRedirectAttrs,
    SetRedirectAttrs,
} from '../../actions/consentActions';
import { RequestActionType, requestError, requestSuccess } from '../../actions/requestActions';
import getUserConsentForClient, { setUserConsentForClient } from '../../api/consents';
import { getClientInfo } from '../../api/oauthClients';
import { accessTokenLocalStoragePath } from '../../constants';
import { ClientInfo, Consent } from '../../models/commonModels';
import RequestActions from '../../models/RequestTypes';
import { RequestType } from '../../reducers/requestReducer';
import { getQueryParameter, getUrlVars } from '../../utils';
import authSaga from '../authSaga';
import { toErrorType } from '../isErrorType';
import { redirectWithQuery } from '../loginAndRegister/loginSaga';

const setUrl = (isHbsUser: boolean, redirectUri: string, state?: string): string => {
    const ssoParam = getQueryParameter('sso');
    const decodedUri = decodeURIComponent(redirectUri);
    const querySeparator = decodedUri.split('?').length > 1 ? '&' : '?';

    const constructUrl = (params: string): string => {
        return `${decodedUri}${querySeparator}hbs=${isHbsUser}${params}`;
    };
    const stateParam: string = state ? `&state=${state}` : '';
    if (ssoParam) {
        return constructUrl(`&sso=true${stateParam}`);
    }
    return constructUrl(stateParam);
};

const redirectToClient = (redirectUri: string, state?: string): void => {
    const token = localStorage.getItem(accessTokenLocalStoragePath);
    let isHbsUser = false;
    if (token) {
        const parsedToken = JSON.parse(atob(token.split('.')[1]));
        isHbsUser = parsedToken && !!parsedToken.hbs ? parsedToken.hbs : false;
    }
    const url = setUrl(isHbsUser, redirectUri, state);
    window.location.assign(url);
};

type RegisterConsentSaga = Generator<
    CallEffect<void> | CallEffect<{ redirect_uri: string }> | RequestActions,
    void,
    { redirect_uri: string }
>;
export function* registerConsentSaga({ payload }: RegisterConsent): RegisterConsentSaga {
    try {
        yield call(authSaga);
        const response = yield call(setUserConsentForClient, payload.client, payload.consent);
        redirectToClient(response.redirect_uri, payload.client.state);
        yield put(requestSuccess(RequestType.RegisterConsent, RequestActionType.Success));
    } catch (error) {
        const errorAsErrorType = toErrorType(error);
        yield put(requestError(errorAsErrorType, RequestType.RegisterConsent, RequestActionType.Error));
    }
}

type RefreshUserConsentForClientSaga = Generator<
    | CallEffect<ClientInfo>
    | CallEffect<void>
    | CallEffect<Consent>
    | PutEffect<SetRedirectAttrs>
    | PutEffect<SetClientInfo>
    | PutEffect<RegisterConsent>
    | PutEffect<GetUserConsentForClient>
    | RequestActions,
    void,
    ClientInfo & Consent
>;
export function* getConsentForClientSaga(): RefreshUserConsentForClientSaga {
    const searchQuery = window.location.search;
    try {
        yield call(authSaga);
        const redirectAttrs = getUrlVars(searchQuery);
        yield put(setRedirectAttrs(redirectAttrs));
        const consent = yield call(getUserConsentForClient, redirectAttrs);
        const clientIsGoogleHome = redirectAttrs?.client_id === 'cf4ba0e0-257a-49fd-8fa4-2d87dbced3af';
        // Google home requires the user to consent every time they login
        if (!clientIsGoogleHome && consent?.scope && consent?.scope.length > 0) {
            yield put(registerConsent({ client: redirectAttrs, consent }));
        } else {
            const validAttrs = yield call(getClientInfo, redirectAttrs);
            yield put(setClientInfo(validAttrs));
            redirectWithQuery('/authorize');
        }
        yield put(requestSuccess(RequestType.GetUserConsentForClient, RequestActionType.Success));
    } catch (error) {
        const errorAsErrorType = toErrorType(error);
        yield put(requestError(errorAsErrorType, RequestType.GetUserConsentForClient, RequestActionType.Error));
    }
}

export default function* consentSaga(): Generator {
    yield all([
        takeEvery(ConsentActionType.GetUserConsentForClientInit, getConsentForClientSaga),
        takeEvery(ConsentActionType.RegisterConsentInit, registerConsentSaga),
    ]);
}
